/**
 * UNDRGRND first-party behavioral events (demo plays + add-to-cart).
 *
 * Sends beacons to the ab-collect Worker's /event endpoint, which writes to
 * the `ug_events` Analytics Engine dataset (see cloudflare/README.md for the
 * schema and readout queries). Key metric this enables: preview-to-purchase —
 * which packs get listened to but not bought.
 *
 * Design notes:
 * - "play" fires once a track has actually been listened to for 3 seconds
 *   (skipped/aborted plays and playlist flick-through don't count), deduped
 *   per track per session.
 * - "atc" fires on add-to-cart interactions, deduped per product per session.
 * - Visitor id: reuses the A/B test's `_usab` cookie id when present so event
 *   rows join with `_ab_vid` order attributes; otherwise its own `_ugev` id.
 *   The id is also written to cart attribute `_ug_vid`, so every order can be
 *   joined back to what that visitor previewed.
 * - Bots: UA/webdriver-gated here; datacenter ASNs filtered server-side.
 *
 * Meta forwarding: "play" beacons also carry the Meta browser identifiers
 * (`_fbp` / `_fbc`), a per-event id, and a marketing-consent flag, so the
 * Worker can relay the play to Meta's Conversions API as a custom `DemoPlay`
 * event. A three-second listen is a much better intent signal than ViewContent
 * for a sample store, and Shopify's own Meta channel cannot send it. Only
 * "play" is forwarded: Meta already receives AddToCart from Shopify, and
 * sending our own copy would put two independent senders on one event and
 * break deduplication.
 */
(function () {
  'use strict';

  var COLLECT = 'https://ab.undrgrndsounds.com/event';
  var AUDIO_ID = 'ug-audio-element';
  var PLAY_SECONDS = 3;

  var UA = navigator.userAgent || '';
  if (
    navigator.webdriver === true ||
    /bot|crawl|spider|slurp|headless|lighthouse|prerender|bingpreview|facebookexternalhit|pingdom|gtmetrix|ptst|petalbot|ahrefs|semrush|mj12|yandex|duckduck/i.test(UA)
  ) {
    return;
  }

  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function randomId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return String(Date.now()) + '-' + Math.random().toString(16).slice(2);
  }

  function visitorId() {
    try {
      var ab = JSON.parse(readCookie('_usab'));
      if (ab && ab.id) return ab.id;
    } catch (e) {}
    var own = readCookie('_ugev');
    if (own) return own;
    var id = randomId();
    document.cookie =
      '_ugev=' + encodeURIComponent(id) +
      '; path=/; max-age=' + 365 * 86400 + '; SameSite=Lax; Secure';
    return id;
  }

  var vid = visitorId();

  function sessionOnce(key) {
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
      return true;
    } catch (e) {
      return true; // private mode: send rather than drop
    }
  }

  /* Meta's click id is normally in the `_fbc` cookie, but the pixel only
     writes it once it has run. On the first pageview of an ad click the
     cookie can still be missing while `fbclid` is right there in the URL, so
     rebuild it in Meta's documented `fb.1.<ms>.<fbclid>` shape. */
  function fbClickId() {
    var cookie = readCookie('_fbc');
    if (cookie) return cookie;
    var match = location.search.match(/[?&]fbclid=([^&]+)/);
    return match ? 'fb.1.' + Date.now() + '.' + decodeURIComponent(match[1]) : '';
  }

  /* Relaying to Meta is a marketing purpose, so it needs affirmative consent.
     If the Customer Privacy API cannot be read we send nothing rather than
     assuming permission — the first-party row is still written either way.
     Plays only fire after three seconds of audio, by which point Shopify's
     privacy script has long since loaded, so little is lost to this. */
  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && cp.marketingAllowed && cp.marketingAllowed());
    } catch (e) {
      return false;
    }
  }

  /* Meta matches far better with an email attached. We only ever have one if
     the visitor identified themselves: logged in, or handed it to the 30%-off
     popup. It is sent raw to our own endpoint over HTTPS and hashed there
     before it goes to Meta, so no address is ever written to our analytics. */
  function knownEmail() {
    try {
      // theme.liquid already publishes this for logged-in customers.
      var shop = window.Shopify && window.Shopify.customer_email;
      if (shop && shop.indexOf('@') > 0) return shop.toLowerCase().trim();
    } catch (e) {}
    try {
      var raw = readCookie('__kla_id');
      if (raw) {
        var o = JSON.parse(atob(raw));
        var k = o.$email || o.email;
        if (k && String(k).indexOf('@') > 0) return String(k).toLowerCase().trim();
      }
    } catch (e) {}
    return '';
  }

  /* `_shopify_y` goes first because it is almost certainly the value Shopify
     already sends as external_id on Purchase — same unhashed UUID shape, and
     it is on 100% of their purchase events. Matching it is what lets Meta
     thread a demo listen to the order that follows. Ours rides along as a
     fallback in case that assumption is wrong. */
  function externalIds() {
    var ids = [];
    var sy = readCookie('_shopify_y');
    if (sy) ids.push(sy);
    if (vid && ids.indexOf(vid) === -1) ids.push(vid);
    return ids;
  }

  function send(event, handle, track) {
    if (!handle) return;
    var payload = JSON.stringify({
      e: event,
      handle: handle,
      track: track || '',
      vid: vid,
      path: location.pathname,
      eid: randomId(),
      fbp: readCookie('_fbp') || '',
      fbc: fbClickId(),
      xid: externalIds(),
      em: knownEmail(),
      mk: marketingAllowed() ? 1 : 0
    });
    // No Content-Type on purpose: keeps the cross-origin beacon a
    // "simple request" so no CORS preflight is needed.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(COLLECT, new Blob([payload]));
    } else {
      fetch(COLLECT, { method: 'POST', body: payload, keepalive: true }).catch(function () {});
    }
  }

  function handleFromUrl(url) {
    var match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  /* Orders inherit cart attributes; _ug_vid lets order exports join back to
     the preview events of the visitor who placed the order. */
  var cartTagged = false;
  function tagCart() {
    if (cartTagged) return;
    cartTagged = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || (cart.attributes && cart.attributes._ug_vid === vid)) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _ug_vid: vid } })
        });
      })
      .catch(function () { cartTagged = false; });
  }

  /* ---- Demo plays ---- */

  function initPlayTracking() {
    var audio = document.getElementById(AUDIO_ID);
    if (!audio) return;

    var current = { src: '', reported: false };

    function playerTrackInfo() {
      var root = document.getElementById('ug-audio-player');
      var link = root && root.querySelector('[data-ug-audio-title-link]');
      return {
        handle: handleFromUrl(link && link.href),
        track: (link && link.textContent || '').trim()
      };
    }

    audio.addEventListener('timeupdate', function () {
      if (audio.currentSrc !== current.src) {
        current = { src: audio.currentSrc, reported: false };
      }
      if (current.reported || !current.src || audio.currentTime < PLAY_SECONDS) return;
      current.reported = true;

      var info = playerTrackInfo();
      if (!info.handle) return;
      if (sessionOnce('_uge_p_' + info.handle + '|' + info.track)) {
        send('play', info.handle, info.track);
        tagCart();
      }
    });
  }

  /* ---- Add to cart ---- */

  function productHandleFromPage() {
    return handleFromUrl(location.pathname);
  }

  function reportAtc(handle) {
    if (!handle) return;
    if (sessionOnce('_uge_a_' + handle)) {
      send('atc', handle);
      tagCart();
    }
  }

  function initAtcTracking() {
    // PDP + bundle hero forms post to /cart/add.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form && /\/cart\/add/.test(form.getAttribute('action') || '')) {
        reportAtc(productHandleFromPage());
      }
    }, true);

    // Player bar ATC button + card/list quick-add buttons.
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-ug-audio-atc]')) {
        var root = document.getElementById('ug-audio-player');
        var link = root && root.querySelector('[data-ug-audio-title-link]');
        reportAtc(handleFromUrl(link && link.href));
        return;
      }
      var quickAdd = e.target.closest('[data-ug-quick-atc], .ug-card-atc');
      if (quickAdd) {
        reportAtc(
          quickAdd.getAttribute('data-ug-product-handle') ||
          handleFromUrl(quickAdd.getAttribute('data-ug-audio-product-url'))
        );
      }
    }, true);
  }

  function init() {
    initPlayTracking();
    initAtcTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
